import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Search, UserCheck, Unlink } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

export function AffiliateLinkDialog({ coupon, onClose, onDone }) {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => { toast.error("Failed to load customers"); setUsers([]); });
  }, []);

  const needle = q.trim().toLowerCase();
  const matches = needle.length < 2 || !users ? [] : users
    .filter((u) => !u.is_blocked && (u.email.toLowerCase().includes(needle) || (u.name || "").toLowerCase().includes(needle)))
    .slice(0, 8);

  const link = async (userId) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/coupons/${coupon.id}/affiliate-account`, { user_id: userId });
      toast.success(userId ? `${coupon.code} linked to ${data.affiliate_user_email}` : `${coupon.code} unlinked`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update affiliate account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="affiliate-link-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-sm uppercase tracking-wide">Link account — {coupon.code}</DialogTitle>
          <DialogDescription className="text-xs">
            Connect this affiliate code to an existing customer account so commissions can be paid as reward points and the affiliate can track earnings under My Orders.
          </DialogDescription>
        </DialogHeader>

        {coupon.affiliate_user_id && (
          <div className="flex items-center justify-between gap-3 bg-[#EAF2EC] border border-[#2D6A4F]/30 rounded-md px-4 py-3" data-testid="affiliate-link-current">
            <p className="text-sm text-[#1A1A1A] flex items-center gap-2 min-w-0">
              <UserCheck className="w-4 h-4 text-[#2D6A4F] shrink-0" />
              <span className="truncate">{coupon.affiliate_user_name || "—"} · <span className="text-[#6E675F]">{coupon.affiliate_user_email}</span></span>
            </p>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => link(null)} data-testid="affiliate-unlink-button" className="shrink-0">
              <Unlink className="w-3.5 h-3.5 mr-1" /> Unlink
            </Button>
          </div>
        )}

        <div className="relative">
          <Search className="w-4 h-4 text-[#9A9088] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="affiliate-link-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by email or name…" className="pl-9" />
        </div>

        <div className="max-h-72 overflow-y-auto divide-y divide-[#EFEBE6] border border-[#DDD7CF] rounded-md" data-testid="affiliate-link-results">
          {users === null && <p className="text-xs text-[#6E675F] p-4">Loading customers…</p>}
          {users && needle.length < 2 && <p className="text-xs text-[#6E675F] p-4">Type at least 2 characters to search.</p>}
          {users && needle.length >= 2 && matches.length === 0 && <p className="text-xs text-[#6E675F] p-4" data-testid="affiliate-link-no-match">No customers match “{q}”.</p>}
          {matches.map((u) => (
            <button
              key={u.user_id}
              type="button"
              disabled={saving || u.user_id === coupon.affiliate_user_id}
              onClick={() => link(u.user_id)}
              data-testid={`affiliate-link-user-${u.email}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F7F5F2] transition-colors disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[#1A1A1A] truncate">{u.name || u.email}</span>
                <span className="block text-xs text-[#6E675F] truncate">{u.email}</span>
              </span>
              <span className="text-xs text-[#6E675F] shrink-0">{u.orders_count} order{u.orders_count === 1 ? "" : "s"} · {u.points.toLocaleString()} pts</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
